import { useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { AI, ArrowRight, Edit } from 'almanac-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { Modal } from 'components/Wizards/Wizard/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { Button } from 'blaze-ui/Button/Button'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ourUuid } from 'utils'
import { reportError } from 'utils/error'
import { Field } from '../Field/Field'
import { Form } from '../Form/Form'
import { OutlineEditor } from './OutlineEditor/OutlineEditor'
import { OutlineEditorHandle } from './OutlineEditor/Types'
import { generateIntroduction } from '../generateIntroduction'
import { generateOutline } from '../generateOutline'
import { generateTitles } from '../generateTitles'
import { TextFieldLoadingState } from 'components/Wizards/TextFieldLoadingState/TextFieldLoadingState'
import { OutlineLoadingState } from 'components/Wizards/OutlineLoadingState/OutlineLoadingState'
import { TitlesLoadingState } from './TitlesLoadingState'
import { YoutubeScriptWizardProps } from './Types'
import { AlmanacFile, GeneratePrompt } from 'types'
import Styles from './YoutubeScriptWizard.module.scss'
import { object } from 'yup'
import { FormSchema } from '../Wizard/Form/Types'
import { Wizard as BaseWizard } from '../Wizard/Wizard'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { filename } from '../schemas/shared/filename'
import { YoutubeScriptSchema as schema } from '../schemas/YoutubeScript'
import { project } from '../schemas/shared/project'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { ConditionalInlineLanguageInputField as InlineLanguageInputField } from '../Wizard/Form/LanguageInputField/ConditionalLanguageInputField'
import { Header } from 'components/Wizards/Wizard/Header/Header'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from '../Wizard/Header/MobileHeader'
import { YouTube } from 'blaze-ui/icons/20'
import { TextChannels } from '../../../constants'
import { WizardProvider } from 'components/Wizards/Wizard/WizardContext'

type Uuid = string
type Identifiable<T> = [Uuid, T]

interface Values {
  topic: string
  goal: string
  audience: string
  video_length: string
  brand_voice_id: number | undefined
  tone: Array<Tone>
  promotion: string
  call_to_action: string
  avoid: string
  language?: string
  project?: AlmanacFile
  filename: string
  brand_kit_id?: number
}

const YoutubeScriptWizard = ({
  initialValues = {},
  onSuccess,
  nodeSelection,
  articleId,
  layerId,
  children,
}: YoutubeScriptWizardProps) => {
  const [currentStep, setCurrentStep] = useState(1)
  const workspaceId = useCurrentWorkspaceId()
  const { openModal, closeModal } = useModals()
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const isCreatingNewFile = !articleId

  const validationSchema = isCreatingNewFile ? (schema.concat(object({ filename, project })) as FormSchema) : schema
  const [formValues, setFormValues] = useState<Values>(
    validationSchema.cast({ ...schema.getDefault(), ...initialValues }) as Values,
  )
  const [introduction, setIntroduction] = useState(initialValues.introduction || '')
  const [outline, setOutline] = useState(initialValues.outline || '')
  const [titleOptions, setTitleOptions] = useState<Array<Identifiable<string>>>([])
  const [selectedTitleId, setSelectedTitleId] = useState<Uuid>()
  const title = titleOptions.find(([uuid]) => uuid === selectedTitleId)?.[1] || ''

  const generateTitlesMutationAbortController = useRef<AbortController | null>(null)
  if (generateTitlesMutationAbortController.current === null)
    generateTitlesMutationAbortController.current = new AbortController()
  const generateTitlesMutation = useMutation(generateTitles)

  const generateIntroductionMutationAbortController = useRef<AbortController | null>(null)
  if (generateIntroductionMutationAbortController.current === null)
    generateIntroductionMutationAbortController.current = new AbortController()
  const generateIntroductionMutation = useMutation(generateIntroduction)

  const generateOutlineMutationAbortController = useRef<AbortController | null>(null)
  if (generateOutlineMutationAbortController.current === null)
    generateOutlineMutationAbortController.current = new AbortController()
  const generateOutlineMutation = useMutation(generateOutline)

  const { generateContent, generateDocMutation: generateScriptMutation } = useGenerateContent({
    generationType: ChannelType.YoutubeScript,
    articleId,
    layerId,
    nodeSelection,
    onSuccess,
  })

  const outlineEditor = useRef<OutlineEditorHandle | null>(null)

  const addTitleOption = () => {
    setTitleOptions((oldTitleOptions) => {
      return [...oldTitleOptions, [ourUuid(), '']]
    })
  }

  const updateTitleOption = (newTitleOption: Identifiable<string>) => {
    setTitleOptions((oldTitleOptions) => {
      return oldTitleOptions.map((t) => {
        if (t[0] === newTitleOption[0]) return newTitleOption
        return t
      })
    })
  }

  const generateTitleOptions = async (payload: Values) => {
    setTitleOptions([])
    generateTitlesMutation.mutate(
      {
        payload: {
          prompt: {
            ...payload,
            generation_type: ChannelType.YoutubeScript,
          },
          workspaceId: workspaceId,
        },
        signal: generateTitlesMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          const titleOptions: Array<Identifiable<string>> = data.map((t) => [ourUuid(), t])
          setTitleOptions(titleOptions)
          setSelectedTitleId(titleOptions[0][0])
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              goToPreviousStep()
            },
            onTryAgain: () => {
              closeModal()
              generateTitleOptions(payload)
            },
          })
        },
      },
    )
  }

  const generateIntro = async () => {
    setIntroduction('')
    generateIntroductionMutation.mutate(
      {
        payload: {
          prompt: {
            ...formValues,
            title,
            generation_type: ChannelType.YoutubeScript,
          },
          title,
          workspaceId: workspaceId,
        },
        signal: generateIntroductionMutationAbortController.current?.signal,
      },
      {
        onSuccess(data) {
          setIntroduction(data ?? '')
        },
        onError(error) {
          reportError(error)
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              setCurrentStep(2)
            },
            onTryAgain: () => {
              closeModal()
              generateIntro()
            },
          })
        },
      },
    )
  }

  const generateHeaders = async () => {
    outlineEditor.current?.clear()
    setOutline('')
    generateOutlineMutation.mutate(
      {
        payload: {
          prompt: {
            ...formValues,
            title,
            generation_type: ChannelType.YoutubeScript,
          },
          title,
          workspaceId: workspaceId,
        },
        signal: generateOutlineMutationAbortController.current?.signal,
      },
      {
        onSuccess: async (data) => {
          setOutline(data)
          outlineEditor.current?.replaceHTMLString(data)
        },
        onError(error) {
          openModal(AiErrorModal, {
            onCancel: () => {
              closeModal()
              setCurrentStep(2)
            },
            onTryAgain: () => {
              closeModal()
              generateHeaders()
            },
          })
          reportError(error)
        },
      },
    )
  }

  const submitStepTwo = () => {
    setCurrentStep(3)
    generateIntro()
    generateHeaders()
  }

  const submitStepThree = async (
    additionalPayload?: Partial<
      GeneratePrompt & { filename?: string; project?: AlmanacFile; articleId?: string | null }
    >,
  ) => {
    const { filename, ...prompt } = formValues
    let payload: Partial<GeneratePrompt & { filename?: string; project?: AlmanacFile }> = {
      project: prompt.project,
      filename,
      generation_type: ChannelType.YoutubeScript,
      ...prompt,
      title,
      introduction,
      format:
        `The output document should be structured in this exact format, ` +
        `which is provided as HTML and should be converted to Markdown, ` +
        `filling in content under the headers as needed:\n${outline}`,
    }

    return generateContent({ ...payload, ...additionalPayload })
  }

  const goToPreviousStep = () => setCurrentStep((s) => s - 1)

  return (
    <WizardProvider schema={schema}>
      <Modal.Root>
        <ResponsiveDesign.Desktop>
          <Header
            steps={
              isTabletPortrait
                ? ['Goals', 'Title', 'Outline', 'Script']
                : ['Purpose & Goals', 'Video Title', 'Script Outline', 'Script']
            }
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            title={TextChannels[ChannelType.YoutubeScript].name}
            icon={YouTube}
            previewUrl="https://res.cloudinary.com/almanac/image/upload/v1710183792/Home%20Card%20Previews/yeflu46q2d5upxn1dcu3.png"
          />
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <MobileHeader
            steps={['Goals', 'Title', 'Outline', 'Script']}
            currentStep={currentStep}
            onStepPress={setCurrentStep}
            title={TextChannels[ChannelType.YoutubeScript].name}
            icon={YouTube}
          />
        </ResponsiveDesign.Mobile>
        {currentStep === 1 && (
          <BaseWizard.Form
            schema={validationSchema}
            onSubmit={(values: Values) => {
              generateTitleOptions(values)
              setCurrentStep(2)
              setFormValues(values)
            }}
            initialValues={formValues}
          >
            <ResponsiveDesign.Desktop>
              <InlineLanguageInputField />
            </ResponsiveDesign.Desktop>
            <Form.SubmitButton>Generate Titles</Form.SubmitButton>
          </BaseWizard.Form>
        )}

        {currentStep === 2 && (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields>
                  <Heading level={4}>Pick a title for your post, or write your own</Heading>
                  {generateTitlesMutation.isLoading && <TitlesLoadingState />}
                  {!generateTitlesMutation.isLoading && (
                    <RadioGroup
                      aria-label="Title"
                      value={selectedTitleId}
                      className={Styles.radioGroup}
                      onChange={(selectedUuid) => {
                        setSelectedTitleId(selectedUuid)
                      }}
                    >
                      {titleOptions.map((titleOption, i) => {
                        const [uuid, value] = titleOption
                        return (
                          <Radio className={Styles.titleOption} value={uuid} aria-label={value} key={uuid} hideRadio>
                            {(radioBulletProps) => (
                              <>
                                <div />
                                <div className={Styles.inputLabel}>Title #{i + 1}</div>
                                <div>
                                  <RadioBullet {...radioBulletProps} size={20} />
                                </div>
                                <div>
                                  <Input
                                    isTextarea
                                    className={Styles.boldInput}
                                    size="lg"
                                    name={`Title ${i + 1}`}
                                    value={value}
                                    onChange={(e) => updateTitleOption([uuid, e.target.value])}
                                    autoFocus={value === ''}
                                    aria-label="Generated Title"
                                    placeholder="Write your own title..."
                                    onKeyDown={(e) => {
                                      // Prevent up and down from selectin other radio options
                                      e.stopPropagation()
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </Radio>
                        )
                      })}
                    </RadioGroup>
                  )}
                  <div className={Styles.titleOptionsFieldsActions}>
                    <Button
                      variant="tertiary"
                      size="lg"
                      frontIcon={Edit}
                      onPress={() => addTitleOption()}
                      isDisabled={generateTitlesMutation.isLoading}
                    >
                      Write Your Own
                    </Button>
                  </div>
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>

            <Form.Actions isSticky>
              {!isMobile && (
                <Form.PreviousButton
                  onPress={() => {
                    generateTitlesMutationAbortController.current?.abort()
                    generateTitlesMutationAbortController.current = new AbortController()
                    goToPreviousStep()
                  }}
                >
                  Previous Step
                </Form.PreviousButton>
              )}

              <Form.PrimaryActions>
                <Button
                  variant="tertiary"
                  size="lg"
                  onPress={() => generateTitleOptions(formValues)}
                  isDisabled={generateTitlesMutation.isLoading}
                  fullWidth={isMobile}
                >
                  Generate New Titles
                </Button>
                <Button
                  size="lg"
                  onPress={() => submitStepTwo()}
                  endIcon={ArrowRight}
                  isDisabled={generateTitlesMutation.isLoading}
                  fullWidth={isMobile}
                >
                  Generate Outline
                </Button>
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}

        {currentStep === 3 && (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields>
                  <Heading level={4}>Edit your outline as desired</Heading>
                  <Field>
                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Title</div>
                      <Input
                        isTextarea
                        className={Styles.boldInput}
                        name="title"
                        value={title}
                        onChange={(e) => {
                          if (!selectedTitleId) return
                          updateTitleOption([selectedTitleId, e.target.value])
                        }}
                      />
                    </div>

                    <div className={Styles.inputField}>
                      <div className={Styles.inputLabel}>Introduction</div>
                      {generateIntroductionMutation.isLoading && (
                        <TextFieldLoadingState lines={3} variant="secondary" />
                      )}
                      {generateIntroductionMutation.isSuccess && (
                        <>
                          <Input
                            isTextarea
                            rows={4}
                            name="introduction"
                            value={introduction}
                            onChange={(e) => setIntroduction(e.target.value)}
                          />
                          <div className={Styles.inputActions}>
                            <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateIntro()}>
                              Regenerate Intro
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Field>

                  <Field
                    label="This is your script’s outline, with numbered sections and bulleted subtopics."
                    description={
                      <>
                        Press <kbd>enter</kbd> to add new sections and use <kbd>tab</kbd> <kbd>shift + tab</kbd> to
                        indent / unindent lines.
                      </>
                    }
                  >
                    {generateOutlineMutation.isLoading && <OutlineLoadingState />}
                    {generateOutlineMutation.isSuccess && (
                      <>
                        <OutlineEditor
                          ref={outlineEditor}
                          aria-label="Outline"
                          className={Styles.outlineEditor}
                          initialHtmlString={outline}
                          onChange={setOutline}
                        />

                        <div className={Styles.inputActions}>
                          <Button variant="tertiary" size="lg" frontIcon={AI} onPress={() => generateHeaders()}>
                            Regenerate Headers
                          </Button>
                        </div>
                      </>
                    )}
                  </Field>
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>

            <Form.Actions isSticky>
              {!isTabletPortrait && (
                <Form.PreviousButton
                  onPress={() => {
                    generateIntroductionMutationAbortController.current?.abort()
                    generateIntroductionMutationAbortController.current = new AbortController()
                    generateOutlineMutationAbortController.current?.abort()
                    generateOutlineMutationAbortController.current = new AbortController()
                    goToPreviousStep()
                  }}
                >
                  Previous Step
                </Form.PreviousButton>
              )}

              <Form.PrimaryActions>
                {children ? (
                  children({
                    submitForm: submitStepThree,
                    isSubmitting:
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateScriptMutation.isLoading,
                  })
                ) : (
                  <Button
                    size="lg"
                    endIcon={ArrowRight}
                    onPress={() => submitStepThree()}
                    isDisabled={
                      generateOutlineMutation.isLoading ||
                      generateIntroductionMutation.isLoading ||
                      generateScriptMutation.isLoading
                    }
                    fullWidth={isMobile}
                  >
                    Generate Script
                  </Button>
                )}
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )}
      </Modal.Root>
    </WizardProvider>
  )
}

export { YoutubeScriptWizard }
